import { useModal } from 'react-modal-hook'
import { sort } from 'semver'
import { Banner, Button, Spinner, Text } from 'src/components/ui'
import { FactoryGateway } from 'src/services'
import {
  GatewayReleasesModal,
  GatewayVersionRenderer,
} from 'gateway/components'
import { useGatewayReleases } from 'gateway/api'
import { humanDateTime } from 'src/utility/time'
import { useIsGatewayOnline } from 'site/gateway'
import { GatewayIcon } from './GatewayIcon'

type Props = {
  siteId: string
  gateway: FactoryGateway
}

export function GatewayOverview({ gateway, siteId }: Props): JSX.Element {
  const gatewayReleasesQuery = useGatewayReleases()
  const { isOnline, lastSeen } = useIsGatewayOnline(siteId, gateway.id)

  const [showModal, closeModal] = useModal(() => (
    <GatewayReleasesModal onClose={closeModal} />
  ))

  if (gatewayReleasesQuery.isLoading) {
    return <Spinner />
  }

  if (gatewayReleasesQuery.isError) {
    return (
      <Banner
        variant="error"
        rightComponent={
          <Button
            variant="primary"
            title="Retry"
            onClick={() => gatewayReleasesQuery.refetch}
          />
        }
      >
        Filed to fetch gateway releases
      </Banner>
    )
  }

  const latestVersion = sort(
    gatewayReleasesQuery.data.filter(r => !r.isBeta).map(r => r.semver),
  ).pop()

  return (
    <div className="flex w-full flex-col gap-s">
      <div className="flex gap-s px-s">
        <GatewayIcon />
        <div className="flex gap-3xl">
          <div className="flex flex-col gap-s">
            <div className="flex flex-col gap-2xs">
              <Text variant="description">Gateway</Text>
              <Text bold>{gateway.hostname}</Text>
            </div>
            <div className="flex flex-col gap-2xs">
              <Text variant="description">Hostname</Text>
              <Text bold>{gateway.hostname} </Text>
            </div>
          </div>
          <div className="flex flex-col gap-s">
            <div className="flex flex-col gap-2xs">
              <Text variant="description">Gateway ID</Text>
              <Text bold>{gateway.id}</Text>
            </div>
            <div className="flex flex-col gap-2xs">
              <Text variant="description">Version</Text>
              <GatewayVersionRenderer
                bold
                version={gateway.version ?? null}
                latestVersion={latestVersion}
                onUpgradeClick={showModal}
              />
            </div>
          </div>
        </div>
      </div>
      {!isOnline && (
        <Banner variant="warning">
          <div className="flex flex-col gap-3xs px-xs">
            <Text bold variant="description">
              Gateway offline
            </Text>
            <Text variant="description">
              Functionality may be limited. Last seen:{' '}
              <span>{lastSeen ? humanDateTime(lastSeen) : 'Never'}</span>
            </Text>
          </div>
        </Banner>
      )}
    </div>
  )
}
